// The review side of the API (P10, P11): the triage queue, a run, the three decisions, the
// admin settings, and the requester's resolution feedback.

import {
  type AdminSettings,
  AdminSettingsSchema,
  type AutonomyPatchBody,
  type BoardHoldBody,
  type FeedbackBody,
  FeedbackResponseSchema,
  type Me,
  type PromoteBodyInput,
  type RejectBody,
  type RerunBody,
  ReviewResponseSchema,
  type TriageQueueQuery,
  TriageQueueSchema,
  TriageRunDetailSchema,
  TriageStatsSchema,
} from "@hoffman-support/contracts";
import { ApiError } from "./api";

async function parse<T>(res: Response, parser: (v: unknown) => T): Promise<T> {
  if (!res.ok) {
    let failure = { status: res.status, code: "unknown", message: res.statusText };
    try {
      const body = (await res.json()) as {
        error?: { code?: string; message?: string; rules?: string[] };
        issues?: Array<{ path: string; message: string }>;
      };
      failure = {
        ...failure,
        code: body.error?.code ?? "unknown",
        message: body.error?.message ?? res.statusText,
        ...(body.issues ? { issues: body.issues } : {}),
        ...(body.error?.rules ? { rules: body.error.rules } : {}),
      };
    } catch {
      // Non-JSON failure body.
    }
    throw new ApiError(failure);
  }
  return parser(await res.json());
}

function headers(me: Me, idempotencyKey?: string): Record<string, string> {
  return {
    "content-type": "application/json",
    "x-csrf-token": me.csrfToken,
    ...(idempotencyKey ? { "idempotency-key": idempotencyKey } : {}),
  };
}

export function triageList(query: Partial<TriageQueueQuery>) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query))
    if (v !== undefined && v !== "") params.set(k, String(v));
  return fetch(`/api/admin/triage?${params.toString()}`, { credentials: "same-origin" }).then((r) =>
    parse(r, (v) => TriageQueueSchema.parse(v)),
  );
}

export function triageStats() {
  return fetch("/api/admin/triage/stats", { credentials: "same-origin" }).then((r) =>
    parse(r, (v) => TriageStatsSchema.parse(v)),
  );
}

export function triageGet(runId: string) {
  return fetch(`/api/admin/triage/${encodeURIComponent(runId)}`, {
    credentials: "same-origin",
  }).then((r) => parse(r, (v) => TriageRunDetailSchema.parse(v)));
}

function decision(me: Me, runId: string, action: "promote" | "reject" | "rerun", body: unknown) {
  return fetch(`/api/admin/triage/${encodeURIComponent(runId)}/${action}`, {
    method: "POST",
    credentials: "same-origin",
    headers: headers(me),
    body: JSON.stringify(body),
  }).then((r) => parse(r, (v) => ReviewResponseSchema.parse(v)));
}

export const triagePromote = (me: Me, runId: string, body: PromoteBodyInput) =>
  decision(me, runId, "promote", body);
export const triageReject = (me: Me, runId: string, body: RejectBody) =>
  decision(me, runId, "reject", body);
export const triageRerun = (me: Me, runId: string, body: RerunBody) =>
  decision(me, runId, "rerun", body);

export function settingsGet() {
  return fetch("/api/admin/settings", { credentials: "same-origin" }).then((r) =>
    parse(r, (v) => AdminSettingsSchema.parse(v) as AdminSettings),
  );
}

export function settingsAutonomy(me: Me, body: AutonomyPatchBody) {
  return fetch("/api/admin/settings/autonomy", {
    method: "PATCH",
    credentials: "same-origin",
    headers: headers(me),
    body: JSON.stringify(body),
  }).then((r) => parse(r, (v) => AdminSettingsSchema.parse(v)));
}

export function settingsBoardHold(me: Me, body: BoardHoldBody) {
  return fetch("/api/admin/settings/board-hold", {
    method: "PUT",
    credentials: "same-origin",
    headers: headers(me),
    body: JSON.stringify(body),
  }).then((r) => parse(r, (v) => AdminSettingsSchema.parse(v)));
}

export function submitFeedback(me: Me, idempotencyKey: string, id: string, body: FeedbackBody) {
  return fetch(`/api/requests/${encodeURIComponent(id)}/feedback`, {
    method: "POST",
    credentials: "same-origin",
    headers: headers(me, idempotencyKey),
    body: JSON.stringify(body),
  }).then((r) => parse(r, (v) => FeedbackResponseSchema.parse(v)));
}
